import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const LanguageContext = createContext(null);

// Spanish-specific media values for the swmc.com unified build.
// lowratespr.com builds already have these baked into env vars,
// so we only override when the URL-detected lang is 'es'.
const ES_MEDIA = {
  keystepsVideoUrl:    'https://youtube.com/shorts/YXQ7Uwwc-bU',
  keystepsVideoId:     'YXQ7Uwwc-bU',
  bannerVideoUrl:      'https://youtu.be/D-zRft-olJc',
  bannerVideoId:       'D-zRft-olJc',
  // Uses staging or prod thumbnail depending on env
  thankYouThumbnail:
    process.env.REACT_APP_THANK_YOU_THUMBNAIL?.includes('stagingresources')
      ? 'https://stagingresources.swmc.com/swmc-images/Tru-Approval-ES.jpg'
      : 'https://resources.swmc.com/swmc-images/Tru-Approval-ES.jpg',
};

export function LanguageProvider({ children }) {
  const { pathname } = useLocation();

  // 1. Check URL path first (for swmc.com unified build)
  //    Matches /truapproval/es, /truapproval/es/, /truapproval/es/agentName
  const urlLang = /^\/(?:[^/]+\/)?es(\/|$)/.test(pathname) ? 'es' : null;

  // 2. Fall back to build-time env var (for lowratespr.com dedicated Spanish builds)
  const lang = urlLang || process.env.REACT_APP_LANG || 'en';

  const value = useMemo(() => {
    const isEs = lang === 'es';
    // If lang was detected from URL (urlLang), we need to override media config
    // because the swmc.com build only has English media in env vars.
    // If lang came from REACT_APP_LANG (lowratespr.com), env vars already have correct Spanish media.
    const useEsOverride = urlLang === 'es';

    return {
      lang,
      isSpanish: isEs,
      media: {
        keystepsVideoUrl:  useEsOverride ? ES_MEDIA.keystepsVideoUrl  : process.env.REACT_APP_KEYSTEPS_YT_VIDEO,
        keystepsVideoId:   useEsOverride ? ES_MEDIA.keystepsVideoId   : process.env.REACT_APP_KEYSTEPS_YT_VIDEO_ID,
        bannerVideoUrl:    useEsOverride ? ES_MEDIA.bannerVideoUrl    : process.env.REACT_APP_BANNER_YT_VIDEO,
        bannerVideoId:     useEsOverride ? ES_MEDIA.bannerVideoId     : process.env.REACT_APP_BANNER_YT_VIDEO_ID,
        thankYouThumbnail: useEsOverride ? ES_MEDIA.thankYouThumbnail : process.env.REACT_APP_THANK_YOU_THUMBNAIL,
        
        footerInfoUrl: isEs
        ? (
            process.env.REACT_APP_SWMC_FOOTET_INFO_ES
          )
        : process.env.REACT_APP_SWMC_FOOTET_INFO,
      },
    };
  }, [lang, urlLang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// Returns just the language string: 'en' | 'es'
export function useLang() {
  return useContext(LanguageContext).lang;
}

// Returns the full context: { lang, isSpanish, media }
export function useLangContext() {
  return useContext(LanguageContext);
}